<script setup>
import { useAuthStore } from "@/composables/store/admin/useAuthStore";
import { useContractorAuthStore } from "@/composables/store/contractor/useAuthStore";
import { useErrorStore } from "@/composables/store/useErrorStore";
import { useUserAuthStore } from "@/composables/store/user/useAuthStore";
import { useSuccessStore } from "@/composables/store/useSuccessStore";
import ScrollToTop from "@core/components/ScrollToTop.vue";
import initCore from "@core/initCore";
import { initConfigStore, useConfigStore } from "@core/stores/config";
import { hexToRgb } from "@layouts/utils";
import { storeToRefs } from "pinia";
import { onMounted, ref, watch } from "vue";
import { useTheme } from "vuetify";

const { global } = useTheme();

// Initialize core functionality and config
initCore();
initConfigStore();

// Store references
const configStore = useConfigStore();
const errorStore = useErrorStore();
const successStore = useSuccessStore();
const { successMessage } = storeToRefs(successStore);
const { errorMessage, errorTimestamp } = storeToRefs(errorStore);

// State
const isError = ref(false);
const isSuccess = ref(false);

// Auth stores
const adminAuthStore = useAuthStore();
const contractorAuthStore = useContractorAuthStore();
const userAuthStore = useUserAuthStore();

// Watch for error messages
// watch(errorMessage, (value) => {
//   isError.value = !!value;
//   if (!value) errorStore.clearErrorMessage();
// }, { immediate: true });

watch(
  () => ({
    message: errorMessage.value,
    timestamp: errorTimestamp.value,
  }),
  ({ message}) => {
    isError.value = !!message; 
    if (!message) errorStore.clearErrorMessage();
  },
  { immediate: true }
);

// Watch for success messages
watch(successMessage, (value) => {
  isSuccess.value = !!value;
  if (!value) successStore.clearSuccessMessage();
}, { immediate: true });

// Initialize all auth stores on mount
onMounted(async () => {
  try {
    await Promise.all([
      adminAuthStore.initializeAuth(),
      contractorAuthStore.initializeAuth(),
      userAuthStore.initializeAuth()
    ]);

    // Check for password reset success in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('reset') && urlParams.get('reset') === 'success') {
      successStore.setSuccessMessage('Password reset successfully! Please login with your new password.');
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  } catch (error) {
    console.error('Auth initialization error:', error);
  }
});
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(
      global.current.value.colors.primary
    )}`">
      <RouterView />
      <ScrollToTop />

      <!-- Success Snackbar -->
      <VSnackbar v-model="isSuccess" location="bottom right" :timeout="3000" color="success">
        {{ successMessage }}
        <template #actions>
          <VBtn icon size="small" @click="successStore.clearSuccessMessage">
            <VIcon icon="tabler-x" />
          </VBtn>
        </template>
      </VSnackbar>

      <!-- Error Snackbar -->
      <VSnackbar v-model="isError" location="bottom right" :timeout="5000" color="error">
        {{ errorMessage }}
        <template #actions>
          <VBtn icon size="small" @click="errorStore.clearErrorMessage">
            <VIcon icon="tabler-x" />
          </VBtn>
        </template>
      </VSnackbar>

      <div id="nav-drawer"></div>
    </VApp>
  </VLocaleProvider>
</template>

<style scoped>
/* Add any global styles here if needed */
</style>
